import { DEFAULT_CURRENCY_CODE, formatCurrency, round2, round4, toCents } from '../money';
import { transitiveChain } from '../records/transitive';

/**
 * Item, the catalog and the stock ledger (piece 16): the pure rules the API
 * and the screens both read. The catalog entity is `Item`, bare; on-hand is a
 * sum over StockMovement and never a column (decision 0153); the margin floor
 * is sell-side (decision 0034); a status is a plain enum whose members each
 * gate different machinery (decision 0161).
 */
export const ITEM_TYPES = ['Good', 'Service'] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

export const ITEM_STATUSES = ['Active', 'ActiveMature', 'Discontinued', 'Inactive'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

/** What each status gates: new lines, replenishment and whether a replacement is suggested (decision 0161). */
export const ITEM_STATUS_RULES: Readonly<Record<ItemStatus, { acceptsNewLines: 'always' | 'whileStockLasts' | 'never'; replenished: boolean; suggestsReplacement: boolean }>> = {
  Active: { acceptsNewLines: 'always', replenished: true, suggestsReplacement: false },
  ActiveMature: { acceptsNewLines: 'always', replenished: false, suggestsReplacement: true },
  Discontinued: { acceptsNewLines: 'whileStockLasts', replenished: false, suggestsReplacement: true },
  Inactive: { acceptsNewLines: 'never', replenished: false, suggestsReplacement: false },
};

export const STOCK_MOVEMENT_REASONS = ['Receipt', 'Shipment', 'WorkOrderConsumption', 'CreditReturn', 'CreditReturnDropShip', 'Transfer', 'Adjustment', 'Issue', 'IssueReturn'] as const;
export type StockMovementReason = (typeof STOCK_MOVEMENT_REASONS)[number];

/**
 * 🔨 The register row of piece 2: where a StockMovement's source pointer may
 * point. `Adjustment` is the named no-target member, carrying a null id; a
 * writer the register omits is a movement nobody can save.
 */
// 🔒 `Deal` is where a part issued outside any job went (decision 1142); an issue against no Deal carries `Adjustment`.
export const STOCK_MOVEMENT_SOURCES = ['PurchaseOrder', 'Shipment', 'WorkOrder', 'CreditNote', 'StockTransfer', 'StockCount', 'StockRevaluation', 'Bill', 'VendorCredit', 'ExpenseLine', 'Deal', 'Adjustment'] as const;
export type StockMovementSource = (typeof STOCK_MOVEMENT_SOURCES)[number];

export function stockSourceProblem(sourceDocumentType: string, sourceDocumentId: string | null | undefined): string | null {
  if (!(STOCK_MOVEMENT_SOURCES as readonly string[]).includes(sourceDocumentType)) return `${sourceDocumentType} is not a registered source of a stock movement`;
  if (sourceDocumentType === 'Adjustment') return sourceDocumentId ? 'a manual Adjustment points at no document' : null;
  return sourceDocumentId ? null : `a ${sourceDocumentType} movement names the document it came from`;
}

/** What Issue Stock is asked for (decision 1142): a quantity off one site's shelf, a reason, and the Deal it went to where there is one. */
export interface IssueStockInput {
  itemId: string;
  siteId: string;
  quantity: number;
  reason: string;
  dealId?: string | null;
}

/** 🔒 What refuses an issue before anything is read (decision 1142): an item, a site, a positive quantity and a reason, each named. */
export function issueStockProblem(input: Partial<IssueStockInput>): string | null {
  if (!input.itemId) return 'Name the item being issued.';
  if (!input.siteId) return 'Name the site the part is taken from.';
  if (typeof input.quantity !== 'number' || !Number.isFinite(input.quantity) || input.quantity <= 0) return 'Issue a quantity above zero.';
  if (!input.reason || input.reason.trim() === '') return 'Say why the part was taken, so the cost has a reason beside it.';
  return null;
}

/** 🔨 The refusal above the shelf (decision 1142): the item, the site, what is on hand and what was asked, in one sentence. */
export function issueAboveOnHandRefusal(facts: { sku: string; siteName: string; onHand: number; asked: number }): string {
  const count = (value: number) => String(Number(value.toFixed(4)));
  return `Only ${count(facts.onHand)} of ${facts.sku} ${facts.onHand === 1 ? 'is' : 'are'} on hand at ${facts.siteName}, so ${count(facts.asked)} cannot be issued from there; count the shelf or issue from another site.`;
}

/**
 * What Return Stock is asked for (decision 1609): the Issue movement it
 * reverses and how much of that issue comes back. The item, the site and the
 * cost are the issue's own, so nothing else is asked.
 */
export interface ReturnStockInput {
  issueMovementId: string;
  quantity: number;
}

/** 🔒 What refuses a return before anything is read (decision 1609): an issue picked and a positive quantity, each named. */
export function returnStockProblem(input: Partial<ReturnStockInput>): string | null {
  if (!input.issueMovementId) return 'Pick the issue the part is coming back from.';
  if (typeof input.quantity !== 'number' || !Number.isFinite(input.quantity) || input.quantity <= 0) return 'Return a quantity above zero.';
  return null;
}

/** 🔨 The refusal above what the issue still has out (decision 1609): the item, what the issue took, what is still out and what was asked. */
export function returnAboveIssueRefusal(facts: { sku: string; issued: number; returned: number; asked: number }): string {
  const count = (value: number) => String(Number(value.toFixed(4)));
  const out = Math.max(0, facts.issued - facts.returned);
  return `That issue took ${count(facts.issued)} of ${facts.sku} and ${count(out)} ${out === 1 ? 'is' : 'are'} still out, so ${count(facts.asked)} cannot come back against it.`;
}

/** One Issue a Return can reverse, as the Return Stock window lists it: newest first, with what is still out. */
export interface ReturnableIssue {
  movementId: string;
  itemId: string;
  siteId: string;
  issuedAt: string;
  quantity: number;
  returned: number;
  remaining: number;
  unitCost: number;
  reason: string | null;
}

/** The Organization's two margin defaults (decision 0010), whole percentages; a new Item seeds from them and nothing existing moves. */
export const DEFAULT_MARGIN_KEY = 'pricing.defaultMarginPercent';
export const DEFAULT_MINIMUM_MARGIN_KEY = 'pricing.defaultMinimumMarginPercent';
export const PIECE_16_SETTINGS: readonly { key: string; value: number }[] = [
  { key: DEFAULT_MARGIN_KEY, value: 30 },
  { key: DEFAULT_MINIMUM_MARGIN_KEY, value: 15 },
];

/** The seeded category every import row with no category lands on; it cannot be retired (decision 0067). */
export const UNCATEGORISED = 'Uncategorized';

/** The stable key that finds the default category whatever its row is named: the seed, New Item and the retire rule read it (decision 9880; T3388). */
export const UNCATEGORIZED_CATEGORY_KEY = 'uncategorized';

/** A margin, not a markup (decision 0048): price = cost / (1 − margin). */
export function priceAtMargin(cost: number, marginPercent: number): number {
  if (marginPercent >= 100) throw new RangeError('a margin is below 100 percent');
  return round2(cost / (1 - marginPercent / 100));
}

export function marginPercentOf(salesPrice: number, cost: number): number {
  if (salesPrice <= 0) return -100;
  return ((salesPrice - cost) / salesPrice) * 100;
}

export { round2, round4 };

// ---- Stock ----

export interface MovementRow {
  itemId: string;
  /** Empty on a transfer's in-transit leg (decision 0980). */
  siteId: string | null;
  quantityDelta: number;
  unitCost: number;
}

/** A row counts toward on-hand when it is the item's and sits on a shelf: the given site, or any site when none is given. */
function isOnHandRow(m: MovementRow, itemId: string, siteId?: string | null): boolean {
  return m.itemId === itemId && m.siteId !== null && (!siteId || m.siteId === siteId);
}

/**
 * 🔨 On-hand is the StockMovement sum by item, and optionally by site, and is stored nowhere (decision 0153).
 * 🔨 A row with no site is a transfer's in-transit leg and is on hand nowhere (decision 0980), so the
 * company-wide figure sums the sites alone; a reader that wants value including transit sums the rows itself.
 */
export function onHand(movements: readonly MovementRow[], itemId: string, siteId?: string | null): number {
  return round4(movements.filter((m) => isOnHandRow(m, itemId, siteId)).reduce((sum, m) => sum + m.quantityDelta, 0));
}

/** On-hand minus the open allocations, each side converted first where the units differ. */
export function quantityAvailable(onHandQuantity: number, openAllocations: readonly number[]): number {
  return round4(onHandQuantity - openAllocations.reduce((a, b) => a + b, 0));
}

/**
 * Average landed cost on hand: the quantity-weighted average of unitCost over
 * the inbound movements still on hand, walking the newest receipts back until
 * the on-hand quantity is covered. Independent of the cost-basis setting.
 */
export function averageLandedCostOnHand(movements: readonly MovementRow[], itemId: string, siteId?: string | null): number | null {
  const own = movements.filter((m) => isOnHandRow(m, itemId, siteId));
  let remaining = onHand(own, itemId, siteId);
  if (remaining <= 0) return null;
  let cost = 0;
  let counted = 0;
  for (const m of [...own].reverse()) {
    if (m.quantityDelta <= 0) continue;
    const take = Math.min(m.quantityDelta, remaining);
    cost += take * m.unitCost;
    counted += take;
    remaining -= take;
    if (remaining <= 0) break;
  }
  return counted > 0 ? cost / counted : null;
}

/**
 * 🔨 The minimum-margin alarm reads the higher of the average landed cost on
 * hand and the current purchase price (decisions 0034, 0084). It is the Item
 * page's pill over a price already under the floor; a save that would write one
 * is refused by `marginFloorRefusal` instead (decision 3140).
 */
export function marginFloorWarning(salesPrice: number, purchasePrice: number, averageLandedCost: number | null, minimumMarginPercent: number): string | null {
  const basis = Math.max(purchasePrice, averageLandedCost ?? 0);
  if (basis <= 0) return null;
  const floor = priceAtMargin(basis, minimumMarginPercent);
  if (salesPrice + 1e-9 >= floor) return null;
  return `${salesPrice.toFixed(2)} is under the ${minimumMarginPercent}% floor of ${floor.toFixed(2)} on a cost of ${basis.toFixed(2)}`;
}

/**
 * 🔒 The least minimum margin an Item or the Organization's default holds
 * (decision 3140; the owner, 2026-10-11: *"It should always have to meet min
 * margin of 1% or greater. We are not a charity."*).
 */
export const MINIMUM_MARGIN_FLOOR_PERCENT = 1;

/** 🔒 A minimum margin is one percent or more and under 100 (decision 3140); null where it is. */
export function minimumMarginProblem(percent: number): string | null {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) return 'A minimum margin is a number of percent.';
  if (percent < MINIMUM_MARGIN_FLOOR_PERCENT) return `A minimum margin is ${MINIMUM_MARGIN_FLOOR_PERCENT}% or more, so every sale makes money, and ${percent}% is under it.`;
  if (percent >= 100) return 'A minimum margin is under 100%.';
  return null;
}

/** The Organization's stored default as a new Item reads it: never under one percent (decision 3140). */
export function flooredMinimumMargin(percent: number): number {
  return Math.max(MINIMUM_MARGIN_FLOOR_PERCENT, percent);
}

/** The floor an Item's sales price is held to, and the cost it was read from. */
export interface MarginFloor {
  /** The higher of the average landed cost on hand and the purchase price. */
  basis: number;
  /** Which of the two the basis is, so the refusal names the cost it read. */
  basisKind: 'purchasePrice' | 'averageLandedCost';
  /** The least sales price: the basis divided by one minus the minimum margin. */
  floor: number;
  minimumMarginPercent: number;
}

/**
 * 🔒 The floor the minimum-margin alarm reads (decisions 0034, 3140): the higher
 * of the average landed cost on hand and the purchase price, divided by one
 * minus the minimum margin. An Item with no cost basis, a Service with no
 * purchase price among them, has no floor.
 */
export function marginFloorOf(purchasePrice: number, averageLandedCost: number | null, minimumMarginPercent: number): MarginFloor | null {
  const landed = averageLandedCost ?? 0;
  const basis = Math.max(purchasePrice, landed);
  if (!(basis > 0)) return null;
  return { basis, basisKind: landed > purchasePrice ? 'averageLandedCost' : 'purchasePrice', floor: priceAtMargin(basis, minimumMarginPercent), minimumMarginPercent };
}

/** Which of an Item's pricing fields a write changes, so the refusal says what to change. */
export type MarginFloorChange = 'salesPrice' | 'purchasePrice' | 'margin';

const dollars = (amount: number): string => formatCurrency({ amount: toCents(amount), currencyCode: DEFAULT_CURRENCY_CODE });

/**
 * 🔒 The refusal of a sales price under the floor (decision 3140), in the
 * margin alarm's words: the price, the margin, the floor and the cost it read,
 * then what to change. Null where the price clears the floor or there is none.
 */
export function marginFloorRefusal(item: { salesPrice: number; purchasePrice: number; minimumMargin: number }, averageLandedCost: number | null, changed: readonly MarginFloorChange[]): string | null {
  const floor = marginFloorOf(item.purchasePrice, averageLandedCost, item.minimumMargin);
  if (!floor || item.salesPrice + 1e-9 >= floor.floor) return null;
  const cost = floor.basisKind === 'averageLandedCost' ? 'the average landed cost on hand' : 'the purchase price';
  const head = `The sales price of ${dollars(item.salesPrice)} is under the ${floor.minimumMarginPercent}% minimum margin floor of ${dollars(floor.floor)} on ${cost} of ${dollars(floor.basis)}`;
  if (changed.includes('salesPrice')) return `${head}; raise the sales price to ${dollars(floor.floor)} or more.`;
  return `${head}; change the sales price first, then the ${changed.includes('purchasePrice') ? 'purchase price' : 'margin'}.`;
}

/**
 * 🔒 The refusal as a viewer refused cost reads it (decisions 0125, 3140): the
 * sales price they typed and that it is under the floor, never the cost, the
 * margin or the floor, which would hand them the cost back. Null where the
 * write stands.
 */
export function marginFloorRefusalWithoutCost(item: { salesPrice: number; purchasePrice: number; minimumMargin: number }, averageLandedCost: number | null, changed: readonly MarginFloorChange[]): string | null {
  if (marginFloorRefusal(item, averageLandedCost, changed) === null) return null;
  return `The sales price of ${dollars(item.salesPrice)} is under this item’s minimum margin floor; somebody who sees the item’s cost sets its price.`;
}

/**
 * 🔒 The one low-stock rule (decision 11200): a shelf is low when its on-hand
 * is at or under a threshold above zero. A threshold of 5 with 5 on hand is
 * the moment to buy, because the next job takes the shelf under it. Every
 * reader calls this: the stock ledger's rows, the Item and Site tiles, the Low
 * Stock report, its nightly run and Raise Purchase Orders.
 *
 * 🔨 A status that is never replenished is never low (ActiveMature,
 * Discontinued and Inactive): nothing is bought for an Item on its way out or
 * no longer sold. The status is the switch, never a zero written into the
 * threshold. A zero threshold is no judgement at all, so it is never low.
 */
export function lowStock(status: ItemStatus | string, onHandQuantity: number, threshold: number): boolean {
  if (ITEM_STATUS_RULES[status as ItemStatus]?.replenished !== true) return false;
  if (!(threshold > 0)) return false;
  return onHandQuantity <= threshold;
}

/** The suggested replenishment quantity: the reorder quantity when set, else the shortfall to the threshold. */
export function suggestedReorder(onHandQuantity: number, threshold: number, reorderQuantity: number | null | undefined): number {
  if (reorderQuantity !== null && reorderQuantity !== undefined) return reorderQuantity;
  return Math.max(0, round4(threshold - onHandQuantity));
}

// ---- Units and quantities ----

export interface UnitLike {
  id: string;
  familyId: string;
  conversionFactor: number;
  conversionOffset: number;
  allowsFractionalQty: boolean;
}

/** A quantity in one unit of a family, in another of the same family; a cross-family conversion is refused. */
export function convertQuantity(value: number, from: UnitLike, to: UnitLike): number {
  if (from.familyId !== to.familyId) throw new RangeError('a quantity converts within one unit family only');
  if (from.id === to.id) return value;
  const base = value * from.conversionFactor + from.conversionOffset;
  return (base - to.conversionOffset) / to.conversionFactor;
}

/** 🔨 A cross-family unitId change is refused once the Item has history; a same-family change is ordinary editing. */
export function unitChangeProblem(hasHistory: boolean, from: UnitLike, to: UnitLike): string | null {
  if (from.familyId === to.familyId) return null;
  return hasHistory ? 'the unit family cannot change once the Item has lines or movements; a new Item carries the new family' : null;
}

/** The Item page's own check: a minimum and an increment that together admit no valid quantity are refused at save. */
export function saleQuantityProblems(minimum: number | null | undefined, increment: number | null | undefined): string[] {
  const problems: string[] = [];
  if (minimum !== null && minimum !== undefined && minimum <= 0) problems.push('a minimum sale quantity is above zero');
  if (increment !== null && increment !== undefined && increment <= 0) problems.push('a sale quantity increment is above zero');
  if (minimum && increment && !approxEqual(minimum / increment, Math.round(minimum / increment))) problems.push(`a minimum of ${minimum} with an increment of ${increment} admits no valid quantity`);
  return problems;
}

/**
 * The line-refusal path: the typed quantity converted into the Item's unit,
 * then the fractional rule of the unit, the minimum and the increment, one
 * guard for the three rules, never on the typed number.
 */
export function lineQuantityProblem(entered: number, enteredUnit: UnitLike, itemUnit: UnitLike, item: { minimumSaleQuantity?: number | null; saleQuantityIncrement?: number | null }): string | null {
  const quantity = round2(convertQuantity(entered, enteredUnit, itemUnit));
  if (!itemUnit.allowsFractionalQty && !approxEqual(quantity, Math.round(quantity))) return `${quantity} is fractional and the unit is sold whole`;
  if (item.minimumSaleQuantity && quantity + 1e-9 < item.minimumSaleQuantity) return `${quantity} is under the minimum of ${item.minimumSaleQuantity}`;
  if (item.saleQuantityIncrement && !approxEqual(quantity / item.saleQuantityIncrement, Math.round(quantity / item.saleQuantityIncrement))) return `${quantity} is not a multiple of ${item.saleQuantityIncrement}`;
  return null;
}

/** Why a corrected quantity moved: raised to the Item's minimum, or rounded up to its next step (decision 1526). */
export type QuantityCorrectionReason = 'minimum' | 'increment';

/** The quantity a line stands on after the sale guards, in the line's own unit, and whether and why it moved. */
export interface CorrectedQuantity {
  quantity: number;
  moved: boolean;
  reason: QuantityCorrectionReason | null;
}

/** 🔨 Settles the noise a conversion or a multiplication leaves, so 17 × 0.1 reads 1.7 and 12.000000000000002 reads 12. */
const settled = (n: number): number => Math.round(n * 1e9) / 1e9;

/**
 * 🔒 The Item Table's correction (decision 1526), beside the refusal it
 * stands in front of. The typed quantity is converted into the Item's unit as
 * the refusal converts it; under the minimum it becomes the minimum, and off
 * the increment it becomes the next multiple up. The answer is converted back
 * into the line's unit. A quantity already right answers itself unchanged, and
 * a quantity of zero or below is left to the person and the server.
 */
export function correctedQuantity(typed: number, enteredUnit: UnitLike, itemUnit: UnitLike, item: { minimumSaleQuantity?: number | null; saleQuantityIncrement?: number | null }): CorrectedQuantity {
  const unchanged: CorrectedQuantity = { quantity: typed, moved: false, reason: null };
  if (!Number.isFinite(typed) || typed <= 0) return unchanged;
  let quantity = convertQuantity(typed, enteredUnit, itemUnit);
  let reason: QuantityCorrectionReason | null = null;
  const minimum = item.minimumSaleQuantity ?? null;
  if (minimum !== null && minimum > 0 && quantity < minimum && !approxEqual(quantity, minimum)) {
    quantity = minimum;
    reason = 'minimum';
  }
  const increment = item.saleQuantityIncrement ?? null;
  if (increment !== null && increment > 0) {
    const steps = quantity / increment;
    if (!approxEqual(steps, Math.round(steps))) {
      quantity = settled(Math.ceil(steps) * increment);
      reason = reason ?? 'increment';
    }
  }
  if (reason === null) return unchanged;
  return { quantity: settled(convertQuantity(quantity, itemUnit, enteredUnit)), moved: true, reason };
}

// ---- Attributes ----

/** 🔨 baseValue is recomputed from enteredValue, never from the stored base, so a factor edit compounds nothing. */
export function baseValueOf(enteredValue: number, unit: Pick<UnitLike, 'conversionFactor' | 'conversionOffset'>): number {
  return enteredValue * unit.conversionFactor + unit.conversionOffset;
}

/** A base value displayed in another unit, rounded to the significant figures of the entered value. */
export function displayInUnit(baseValue: number, unit: Pick<UnitLike, 'conversionFactor' | 'conversionOffset'>, significantFigures: number): number {
  const value = (baseValue - unit.conversionOffset) / unit.conversionFactor;
  if (value === 0) return 0;
  return Number(value.toPrecision(Math.max(1, significantFigures)));
}

export function significantFiguresOf(entered: number): number {
  const text = Math.abs(entered).toExponential().split('e')[0]!.replace('.', '').replace(/0+$/, '');
  return Math.max(1, text.length);
}

/** 🔨 Comparison of a converted value is a relative-epsilon tolerance, never exact equality. */
export function approxEqual(a: number, b: number, epsilon = 1e-9): boolean {
  const scale = Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) <= epsilon * scale;
}

// ---- Status, substitution and suppliers ----

/** The replacement chain resolves transitively to its end, with a cycle guard, the same rule as approval delegations. */
export function resolveReplacement(itemId: string, replacementOf: (id: string) => string | null | undefined): string | null {
  const chain = transitiveChain(itemId, replacementOf, { label: 'Item.replacedByItemId' });
  return chain.length > 0 ? chain[chain.length - 1]! : null;
}

export type SalesOrderLineCase = 'sells' | 'warnSpecialOrder' | 'convertWithDialog' | 'refused';

/** 🔨 The five cases on a Sales Order line, on-hand being the StockMovement sum; Mature and Discontinued diverge at zero alone. */
export function salesOrderLineCase(status: ItemStatus, onHandQuantity: number, hasReplacement: boolean): SalesOrderLineCase {
  if (status === 'Inactive') return 'refused';
  if (status === 'Active') return 'sells';
  if (onHandQuantity > 0) return 'sells';
  if (status === 'ActiveMature') return 'warnSpecialOrder';
  return hasReplacement ? 'convertWithDialog' : 'refused';
}

/** A replenishment PO (no sales order) suggests the replacement; one tied to a customer order keeps the discontinued part. */
export function purchaseOrderSuggestsReplacement(status: ItemStatus, salesOrderId: string | null): boolean {
  return salesOrderId === null && ITEM_STATUS_RULES[status].suggestsReplacement;
}

/** A supplier row's standing with the business (decision 0337): Primary is exclusive per Item, Blacklisted never reaches an RFQ picker. */
export const ITEM_SUPPLIER_STATUSES = ['Primary', 'Active', 'NotApplicable', 'Blacklisted'] as const;
export type ItemSupplierStatus = (typeof ITEM_SUPPLIER_STATUSES)[number];

export function asItemSupplierStatus(value: unknown): ItemSupplierStatus | null {
  return (ITEM_SUPPLIER_STATUSES as readonly string[]).includes(String(value)) ? (value as ItemSupplierStatus) : null;
}

export interface SupplierLike {
  id?: string;
  companyId: string;
  status: ItemSupplierStatus;
  endDate?: Date | string | null;
}

/** Whether a supplier is the manufacturer is derived, never stored. */
export function isDirectSupplier(supplier: SupplierLike, manufacturerCompanyId: string | null | undefined): boolean {
  return manufacturerCompanyId !== null && manufacturerCompanyId !== undefined && supplier.companyId === manufacturerCompanyId;
}

/**
 * 🔨 Primary is exclusive per Item: setting a row Primary demotes every other
 * Primary row of the Item to Active, in the same write (decision 0337). The
 * answer is the rows to demote, so the caller writes them and nothing else.
 */
export function primaryDemotions<T extends SupplierLike>(existing: readonly T[], candidate: { id?: string; companyId: string }, next: ItemSupplierStatus): T[] {
  if (next !== 'Primary') return [];
  return existing.filter((s) => s.status === 'Primary' && (candidate.id ? s.id !== candidate.id : s.companyId !== candidate.companyId));
}

/** The Primary row defaults a Purchase Order line; an ended row never does. */
export function primarySupplier<T extends SupplierLike>(rows: readonly T[]): T | null {
  return rows.find((s) => s.status === 'Primary' && !s.endDate) ?? null;
}

/** 🔨 A Blacklisted row never offers the vendor in the RFQ picker; every other status does. */
export function offeredOnRfq(status: ItemSupplierStatus): boolean {
  return status !== 'Blacklisted';
}

/** How a Sourcing row reads: the status is its pill, NotApplicable muted, Blacklisted as an error. */
export function supplierRowTone(status: ItemSupplierStatus): { pillGroup: 'good' | 'warning' | 'error' | 'inactive'; muted: boolean } {
  if (status === 'Primary') return { pillGroup: 'good', muted: false };
  if (status === 'Blacklisted') return { pillGroup: 'error', muted: false };
  if (status === 'NotApplicable') return { pillGroup: 'inactive', muted: true };
  return { pillGroup: 'inactive', muted: false };
}

/** Where a defaultable number came from, so the screen says so rather than leaving the first surprising value to a support call. */
export type ValueOrigin = 'Item' | 'Category' | 'Organization' | 'Type';
